<template>
  <div :class="['chat-message', message.role]">
    <div class="message-bubble">
      <!-- Assistant messages: render sanitized HTML -->
      <div
        v-if="message.role === 'assistant'"
        class="message-content rich-html-body"
        v-html="renderedContent"
      ></div>
      <!-- User messages: plain text only -->
      <div v-else class="message-content">{{ message.content }}</div>
      <!-- Citations footer (V1: file names only) -->
      <div v-if="message.citations && message.citations.length > 0" class="message-citations">
        <small><i class="fa fa-file-text-o mr-1"></i>Fontes: {{ message.citations.join(', ') }}</small>
      </div>
      <div class="message-time" v-if="message.timestamp">
        {{ formatTime(message.timestamp) }}
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// marked v17 API: use extensions for custom link rendering
marked.use({
  gfm: true,
  breaks: true,
  renderer: {
    link({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens);
      const titleAttr = title ? ' title="' + title + '"' : '';
      return '<a href="' + href + '"' + titleAttr + ' target="_blank" rel="noopener noreferrer">' + text + '</a>';
    },
  },
});

const HTML_RESPONSE_TAG_RE = /<\/?(section|h2|h3|p|strong|em|ul|ol|li|table|thead|tbody|tr|th|td|blockquote|hr)\b/i;

const SAFE_HTML_TAGS = [
  'section', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'strong', 'b', 'em', 'i', 'u', 'small',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'blockquote', 'hr', 'br',
  'a', 'code', 'pre',
];

const SAFE_HTML_ATTRS = [
  'href', 'title', 'target', 'rel',
  'colspan', 'rowspan', 'scope',
];

/**
 * Remove OpenAI file_search citation annotations that leak into response text.
 * These appear as 【turn0file5】 or similar patterns when the model cites retrieved chunks.
 */
function sanitizeOpenAICitations(content) {
  return content
    .replace(/【\s*turn\d+file\d+\s*】/g, '')
    .replace(/\bturn\d+file\d+\b/g, '')
    .replace(/\[filecite[^\]]*\]/gi, '')
    .replace(/[【】]+/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function stripHtmlCodeFence(content) {
  return content
    .replace(/^```(?:html)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
}

function sanitizeRichHtml(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_HTML_TAGS,
    ALLOWED_ATTR: SAFE_HTML_ATTRS,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'img', 'svg', 'math'],
    FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload', 'onmouseover'],
    ADD_ATTR: ['target', 'rel'],
  });
}

export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  computed: {
    renderedContent() {
      if (this.message.role !== 'assistant' || !this.message.content) return '';
      const cleaned = stripHtmlCodeFence(sanitizeOpenAICitations(this.message.content));

      if (HTML_RESPONSE_TAG_RE.test(cleaned)) {
        return sanitizeRichHtml(cleaned);
      }

      // Backward compatibility: old stored messages may still be Markdown.
      const html = marked.parse(cleaned);
      return sanitizeRichHtml(html);
    },
  },
  methods: {
    formatTime(timestamp) {
      return moment(timestamp).format('HH:mm');
    },
  },
};
</script>

<style scoped>
.chat-message {
  display: flex;
  margin-bottom: 12px;
  padding: 0 8px;
}

.chat-message.user {
  justify-content: flex-end;
}

.chat-message.assistant {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 12px;
  word-wrap: break-word;
  font-size: 13px;
  line-height: 1.4;
}

/* User messages: keep pre-wrap for plain text */
.chat-message.user .message-bubble {
  background-color: #0b6baf;
  color: #fff;
  border-bottom-right-radius: 4px;
  white-space: pre-wrap;
}

/* Assistant messages: normal whitespace for rendered HTML */
.chat-message.assistant .message-bubble {
  background-color: #f0f0f0;
  color: #333;
  border-bottom-left-radius: 4px;
}

.message-citations {
  margin-top: 6px;
  padding-top: 4px;
  border-top: 1px solid rgba(0,0,0,0.08);
  color: #666;
  font-size: 11px;
  line-height: 1.3;
}

.message-time {
  font-size: 10px;
  opacity: 0.6;
  margin-top: 4px;
  text-align: right;
}
</style>

<!-- Unscoped styles for sanitized GenIA HTML content -->
<style>
.rich-html-body {
  color: #1f2933;
  font-size: 13px;
  line-height: 1.48;
}

.rich-html-body section {
  margin: 0 0 12px;
}

.rich-html-body section:last-child {
  margin-bottom: 0;
}

.rich-html-body h1,
.rich-html-body h2,
.rich-html-body h3 {
  font-size: 15px;
  font-weight: 700;
  margin: 12px 0 7px;
  color: #0b4f7d;
  line-height: 1.25;
}

.rich-html-body h4,
.rich-html-body h5,
.rich-html-body h6 {
  font-size: 13px;
  font-weight: 600;
  margin: 9px 0 5px;
  color: #1f2933;
}

.rich-html-body p {
  margin: 6px 0;
}

.rich-html-body strong {
  font-weight: 700;
}

.rich-html-body em {
  font-style: italic;
}

.rich-html-body ul,
.rich-html-body ol {
  padding-left: 18px;
  margin: 6px 0 8px;
}

.rich-html-body li {
  margin: 3px 0;
}

.rich-html-body a {
  color: #0b6baf;
  text-decoration: underline;
}

.rich-html-body a:hover {
  color: #084a7a;
}

/* Tables */
.rich-html-body table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  margin: 10px 0 12px;
  font-size: 12px;
  display: block;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #c9d8e5;
  border-radius: 8px;
  background: #fff;
}

.rich-html-body thead {
  background-color: #e8f2fb;
}

.rich-html-body th,
.rich-html-body td {
  border: 0;
  border-right: 1px solid #d8e3ec;
  border-bottom: 1px solid #d8e3ec;
  padding: 7px 9px;
  text-align: left;
  vertical-align: top;
  white-space: normal;
  min-width: 120px;
}

.rich-html-body th {
  color: #0b4f7d;
  font-weight: 700;
}

.rich-html-body th:last-child,
.rich-html-body td:last-child {
  border-right: 0;
}

.rich-html-body tr:last-child td {
  border-bottom: 0;
}

.rich-html-body tr:nth-child(even) {
  background-color: #f8fbfd;
}

.rich-html-body tr:hover {
  background-color: #eef7ff;
}

/* Code */
.rich-html-body code {
  background-color: #e8e8e8;
  padding: 1px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.rich-html-body pre {
  background-color: #e8e8e8;
  padding: 8px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 6px 0;
}

.rich-html-body pre code {
  background: none;
  padding: 0;
}

/* Blockquotes */
.rich-html-body blockquote {
  border-left: 4px solid #0b6baf;
  padding: 8px 10px;
  margin: 8px 0;
  color: #425466;
  background: #eef7ff;
  border-radius: 0 6px 6px 0;
}

/* Horizontal rules */
.rich-html-body hr {
  border: none;
  border-top: 1px solid #ddd;
  margin: 8px 0;
}
</style>
